module API.SelEmployer
    ( EmployerID, BaseEmployer, SelEmployer, PosEmployer, PosConEmployer, SelPosition, SelContact
    , PositionID, ContactID
    , employerFields, positionFields, contactFields
    , employerSearch, employerSearchSelAff, employerPosSearchSelAff, employerPosConSearchSelAff
    , positionSelAff, contactSelAff
    , getEmployer
) where

import Common

import API.Pagination (unpaged)
import API.Types (DocumentID, StudentID)
import GraphQL.Query (class QueryResult, Query, buildQuery, nameQuery)
import GraphQL.Type (TypedID)
import Internal.Luxon (LDateTime)
import Widgets.BetterSelect (SearchAff(..), simpleFrontendSearch, wrapSelectOption)


type EmployerID = TypedID "Employer"
type PositionID = TypedID "Position"
type ContactID = TypedID "Contact"

type SelEmployer = BaseEmployer ()
type BaseEmployer r = { id :: EmployerID, company :: String | r }

employerFields :: String
employerFields = "id, company"

type PosEmployer = BaseEmployer ( positions :: Array SelPosition )

type PosConEmployer = BaseEmployer
    ( positions :: Array SelPosition
    , contacts :: Array SelContact
    , deleted_at :: Maybe LDateTime
    )

type SelPosition = { id :: PositionID, label :: String }
positionFields :: String
positionFields = "id, label"

type SelContact = { id :: ContactID, name :: String }
contactFields :: String
contactFields = "id, name"

employerSearchSelAff :: DocumentID -> StudentID -> SearchAff SelEmployer
employerSearchSelAff form student = BackendSearch \search ->
    wrapSelectOption _.company $ employerSearch form student search

employerSearch ::  DocumentID -> StudentID -> String -> Query (Array SelEmployer)
employerSearch form_id student_id search = nameQuery "EmployerSearch" $
    unpaged $ buildQuery "employers" { form_id, student_id, search }

employerPosSearchSelAff :: SearchAff PosEmployer
employerPosSearchSelAff = BackendSearch \search ->
    wrapSelectOption _.company $ employerPosSearch search

employerPosSearch :: String -> Query (Array PosEmployer)
employerPosSearch search = nameQuery "EmployerPositionsSearch" $
    unpaged $ buildQuery "employers" { search }

employerPosConSearchSelAff :: DocumentID -> StudentID -> SearchAff PosConEmployer
employerPosConSearchSelAff fid sid = BackendSearch \search ->
    wrapSelectOption _.company $ employerPosConSearch fid sid search

employerPosConSearch :: DocumentID -> StudentID -> String -> Query (Array PosConEmployer)
employerPosConSearch form_id student_id search = nameQuery "EmployerPositionsContacts" $
    unpaged $ buildQuery "employers" { form_id, student_id, search }

getEmployer :: forall e. QueryResult e => EmployerID -> DocumentID -> StudentID -> Query e
getEmployer id form_id student_id = nameQuery "GetEmployer" $
    buildQuery "employer" { id, student_id, form_id }


-- | Frontend search aff which lazy loads a list of a given employer's
-- | positions from the backend.
positionSelAff :: DocumentID -> StudentID -> EmployerID -> SearchAff SelPosition
positionSelAff form student employer = simpleFrontendSearch _.label $
    wrapSelectOption _.label $ positionList form student employer

positionList :: DocumentID -> StudentID -> EmployerID -> Query (Array SelPosition)
positionList form_id student_id employer = nameQuery "EmployerPositions" $
    process <$> buildQuery "employer" { form_id, student_id, id: employer }
  where
    process :: { positions :: Array SelPosition } -> Array SelPosition
    process = _.positions

-- | Frontend search aff which lazy loads a list of a given employer's
-- | contacts from the backend.
contactSelAff :: DocumentID -> StudentID -> EmployerID -> SearchAff SelContact
contactSelAff form student employer = simpleFrontendSearch _.label $
    wrapSelectOption _.name $ contactList form student employer

contactList :: DocumentID -> StudentID -> EmployerID -> Query (Array SelContact)
contactList form_id student_id employer = nameQuery "EmployerContacts" $
    process <$> buildQuery "employer" { form_id, student_id, id: employer }
  where
    process :: { contacts :: Array SelContact } -> Array SelContact
    process = _.contacts
